import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import PrivacyPage from "./PrivacyPage";
import TermsPage from "./TermsPage";
import { hasSupabase } from "./lib/supabase-client";
import { analytics } from "./lib/analytics";
import { errorTracking } from "./lib/error-tracking";
import { captureReferralCode } from "./lib/referrals";
import "./index.css";

// ── Initialize Analytics & Error Tracking ──
analytics.init();
errorTracking.init();

// ── Capture Referral Code from URL ──
captureReferralCode();

// ── Service Worker Registration ──
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

// ── Track Page View ──
analytics.page(typeof window !== "undefined" ? window.location.pathname : "/");

// ── PWA Install Prompt ──
// Store the beforeinstallprompt event so we can trigger it from the UI
if (typeof window !== "undefined") {
  (window as any).__pwaInstallPrompt = null;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    (window as any).__pwaInstallPrompt = e;
    window.dispatchEvent(new Event("pwa-install-available"));
  });
}

if (typeof window !== "undefined") {
  (window as unknown as { storage: { get: (k: string) => Promise<{ value?: string }>; set: (k: string, v: string) => Promise<void>; delete: (k: string) => Promise<void> } }).storage = {
    get: (key: string) =>
      Promise.resolve({ value: localStorage.getItem(key) ?? undefined }),
    set: (key: string, value: string) => {
      localStorage.setItem(key, value);
      return Promise.resolve();
    },
    delete: (key: string) => {
      localStorage.removeItem(key);
      return Promise.resolve();
    },
  };
}

const root = ReactDOM.createRoot(document.getElementById("root")!);

const LazySupabaseApp = React.lazy(() => import("./SupabaseApp"));

function LoadingFallback() {
  return (
    <div style={{ position: "fixed", inset: 0, background: "#1a2e1a" }} />
  );
}

function ErrorFallback({ error }: { error: Error }) {
  return (
    <div style={{ minHeight: "100vh", padding: 24, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f8f7f4", fontFamily: "'DM Sans', sans-serif", textAlign: "center" }}>
      <div style={{ fontSize: 18, fontWeight: 600, color: "#dc2626", marginBottom: 8 }}>Something went wrong</div>
      <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 16, maxWidth: 400 }}>{error.message}</div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{ padding: "10px 20px", background: "#2d5a27", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
      >
        Reload page
      </button>
    </div>
  );
}

class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error) return <ErrorFallback error={this.state.error} />;
    return this.props.children;
  }
}

function Root() {
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  if (pathname === "/privacy") return <PrivacyPage />;
  if (pathname === "/terms") return <TermsPage />;
  if (hasSupabase) {
    return (
      <AppErrorBoundary>
        <React.Suspense fallback={<LoadingFallback />}>
          <LazySupabaseApp />
        </React.Suspense>
      </AppErrorBoundary>
    );
  }
  return <App />;
}

root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);
